import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, Info, Lightbulb, Minus, Plus, Quote, Stethoscope } from "lucide-react";
import BlogProductCard from "./BlogProductCard";
import { sanitizeHtml } from "../../lib/sanitize";

export const headingId = (block) => `section-${block.id}`;

export const tocFromBlocks = (blocks = []) =>
    blocks.filter((b) => b.type === "heading" && ["2", "3", 2, 3].includes(b.level ?? 2))
        .map((b) => ({ id: headingId(b), text: b.text || "", level: Number(b.level ?? 2) }));

const CALLOUTS = {
    tip: { icon: Lightbulb, cls: "border-lime/40 bg-lime/[0.07]", label: "Tip" },
    info: { icon: Info, cls: "border-gold/40 bg-gold/[0.07]", label: "Important information" },
    warning: { icon: AlertTriangle, cls: "border-orange-400/40 bg-orange-500/[0.07]", label: "Please note" },
};

const Html = ({ html, className = "" }) => (
    <div className={`blog-html ${className}`}
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }} />
);

const Figure = ({ b }) => {
    const width = { inline: "max-w-md", wide: "max-w-3xl", full: "w-full" }[b.width || "full"];
    const align = b.align === "left" ? "mr-auto" : b.align === "right" ? "ml-auto" : "mx-auto";
    const img = (
        <img src={b.url} alt={b.alt || ""} loading="lazy" decoding="async"
            className="w-full h-auto border border-white/10" />
    );
    return (
        <figure className={`my-8 ${width} ${align}`} data-testid={`blog-image-${b.id}`}>
            {b.link ? <a href={b.link} target={b.new_tab ? "_blank" : undefined} rel="noreferrer">{img}</a> : img}
            {b.caption && <figcaption className="mt-2 text-xs text-zinc-500 text-center">{b.caption}</figcaption>}
        </figure>
    );
};

const Faq = ({ b }) => {
    const [open, setOpen] = React.useState(0);
    return (
        <div className="my-8 not-prose" data-testid={`blog-faq-${b.id}`}>
            {b.title && <h3 className="font-display text-2xl uppercase tracking-tight mb-4">{b.title}</h3>}
            <div className="divide-y divide-white/10 border-y border-white/10">
                {(b.items || []).map((item, i) => (
                    <div key={i}>
                        <button onClick={() => setOpen(open === i ? -1 : i)} data-testid={`blog-faq-q-${b.id}-${i}`}
                            className="w-full flex items-center justify-between gap-4 py-4 text-left">
                            <span className="font-semibold">{item.question}</span>
                            {open === i ? <Minus className="w-4 h-4 text-gold shrink-0" />
                                : <Plus className="w-4 h-4 text-gold shrink-0" />}
                        </button>
                        {open === i && <div className="pb-4 text-zinc-300"><Html html={item.answer} /></div>}
                    </div>
                ))}
            </div>
        </div>
    );
};

const embedUrl = (url = "") => {
    const yt = url.match(/(?:youtu\.be\/|v=)([\w-]{6,})/);
    if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
    const vimeo = url.match(/vimeo\.com\/(\d+)/);
    if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
    return url;
};

export const BlogBody = ({ blocks = [], style = "standard", onTrack }) => {
    const prose = {
        standard: "text-[17px] leading-[1.85]",
        editorial: "text-[18px] md:text-[19px] leading-[1.9]",
        medical_guide: "text-[16px] md:text-[17px] leading-[1.8]",
        list_tips: "text-[17px] leading-[1.85]",
        product_education: "text-[17px] leading-[1.85]",
    }[style] || "text-[17px] leading-[1.85]";

    return (
        <div className={`blog-content text-zinc-300 ${prose}`} data-testid="blog-body">
            {blocks.map((b) => {
                switch (b.type) {
                    case "heading": {
                        const level = Number(b.level ?? 2);
                        const Tag = level === 2 ? "h2" : level === 3 ? "h3" : "h4";
                        const cls = level === 2
                            ? "font-display text-3xl md:text-4xl uppercase tracking-tight mt-12 mb-4 text-white"
                            : level === 3 ? "font-display text-2xl uppercase tracking-tight mt-9 mb-3 text-white"
                                : "font-semibold text-lg mt-7 mb-2 text-white";
                        return <Tag key={b.id} id={headingId(b)} className={cls}>{b.text}</Tag>;
                    }
                    case "text":
                        return <Html key={b.id} html={b.html} className="my-5" />;
                    case "image":
                        return <Figure key={b.id} b={b} />;
                    case "gallery":
                        return (
                            <div key={b.id} className="my-8 grid grid-cols-2 md:grid-cols-3 gap-3"
                                data-testid={`blog-gallery-${b.id}`}>
                                {(b.images || []).map((im, i) => (
                                    <figure key={i}>
                                        <img src={im.url} alt={im.alt || ""} loading="lazy"
                                            className="w-full h-40 object-cover border border-white/10" />
                                        {im.caption && <figcaption className="mt-1 text-[11px] text-zinc-500">{im.caption}</figcaption>}
                                    </figure>
                                ))}
                            </div>
                        );
                    case "quote":
                        return (
                            <blockquote key={b.id} data-testid={`blog-quote-${b.id}`}
                                className="my-8 border-l-2 border-gold pl-5 text-xl text-white/90 italic">
                                <Quote className="w-5 h-5 text-gold mb-2" />
                                <Html html={b.html} />
                                {b.cite && <cite className="block mt-3 mono text-[10px] tracking-[0.16em] text-zinc-500 not-italic">— {b.cite}</cite>}
                            </blockquote>
                        );
                    case "tip":
                    case "info":
                    case "warning": {
                        const c = CALLOUTS[b.type];
                        return (
                            <div key={b.id} data-testid={`blog-callout-${b.id}`}
                                className={`not-prose my-8 border ${c.cls} p-5 flex gap-4`}>
                                <c.icon className="w-5 h-5 shrink-0 mt-0.5 text-gold" />
                                <div className="min-w-0">
                                    <div className="mono text-[10px] tracking-[0.18em] text-zinc-400 mb-1">
                                        {(b.title || c.label).toUpperCase()}
                                    </div>
                                    <Html html={b.html} className="text-zinc-300 text-[15px]" />
                                </div>
                            </div>
                        );
                    }
                    case "faq":
                        return <Faq key={b.id} b={b} />;
                    case "table":
                        return (
                            <div key={b.id} className="my-8 overflow-x-auto" data-testid={`blog-table-${b.id}`}>
                                <table className="w-full text-sm border border-white/10">
                                    {!!(b.columns || []).length && (
                                        <thead><tr className="bg-white/5">
                                            {b.columns.map((c, i) => (
                                                <th key={i} className="text-left mono text-[10px] tracking-[0.14em] text-zinc-400 p-3 border-b border-white/10">{c}</th>
                                            ))}
                                        </tr></thead>
                                    )}
                                    <tbody>
                                        {(b.rows || []).map((r, ri) => (
                                            <tr key={ri} className="border-b border-white/5">
                                                {r.map((cell, ci) => <td key={ci} className="p-3 text-zinc-300">{cell}</td>)}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        );
                    case "video":
                        return (
                            <figure key={b.id} className="my-8" data-testid={`blog-video-${b.id}`}>
                                <div className="aspect-video border border-white/10 bg-black">
                                    <iframe src={embedUrl(b.url)} title={b.caption || "Video"} allowFullScreen
                                        loading="lazy" className="w-full h-full" />
                                </div>
                                {b.caption && <figcaption className="mt-2 text-xs text-zinc-500 text-center">{b.caption}</figcaption>}
                            </figure>
                        );
                    case "divider":
                        return <hr key={b.id} className="my-10 border-white/10" />;
                    case "cta":
                        return (
                            <div key={b.id} data-testid={`blog-cta-${b.id}`}
                                className="not-prose my-10 border border-gold/30 bg-gold/[0.06] p-6">
                                {b.title && <div className="font-display text-2xl uppercase tracking-tight">{b.title}</div>}
                                <Html html={b.html} className="text-zinc-300 mt-2 text-[15px]" />
                                {b.label && (
                                    <Link to={b.href || "/"} onClick={() => onTrack?.("cta_click", b.href || "")}
                                        data-testid={`blog-cta-link-${b.id}`}
                                        className="mt-4 inline-flex items-center gap-2 btn-gold px-5 py-2.5 mono
                                                   text-[10px] tracking-[0.16em] font-bold hover:brightness-110">
                                        {b.label} <ArrowRight className="w-3.5 h-3.5" />
                                    </Link>
                                )}
                            </div>
                        );
                    case "product":
                        return <BlogProductCard key={b.id} slug={b.slug} onTrack={onTrack} />;
                    case "consultation":
                        return (
                            <div key={b.id} data-testid={`blog-consultation-${b.id}`}
                                className="not-prose my-10 border border-lime/30 bg-lime/[0.06] p-6">
                                <Stethoscope className="w-6 h-6 text-lime" />
                                <div className="font-display text-2xl uppercase tracking-tight mt-3">
                                    {b.title || "Need personalised guidance?"}
                                </div>
                                <p className="text-zinc-300 mt-2 text-[15px]">
                                    {b.body || "Get a consultation based on your individual concerns."}
                                </p>
                                <Link to={b.area ? `/consultation?area=${b.area}` : "/consultation"}
                                    onClick={() => onTrack?.("consultation_click", b.area || "general")}
                                    data-testid={`blog-consultation-link-${b.id}`}
                                    className="mt-4 inline-flex items-center gap-2 border border-lime text-lime px-5 py-2.5
                                               mono text-[10px] tracking-[0.16em] font-bold hover:bg-lime hover:text-bg0 transition-colors">
                                    {b.label || "Choose your consultation"} <ArrowRight className="w-3.5 h-3.5" />
                                </Link>
                            </div>
                        );
                    default:
                        return null;
                }
            })}
        </div>
    );
};

export default BlogBody;
